/**
 * The expert's own profile. Shows a read-only overview; "Edit profile" at the bottom switches to the
 * form. After sign-up (or whenever the profile is incomplete) the form is shown straight away.
 */
import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { api, ApiError } from '@/api/client'
import { useProfile } from '@/auth/ProfileContext'
import { Avatar, AvatarPicker } from '@/components/Avatar'
import { fmtDay } from '@/lib/format'
import { AreaPicker, ProgramChips } from '@/lib/programs'
import { profileAreas, profileComplete, type Avatar as AvatarData, type User } from '@/lib/types'

export default function SmeProfile() {
  const { profile, setProfile } = useProfile()
  const navigate = useNavigate()
  const location = useLocation() as { state?: { setup?: boolean } }
  const setup = Boolean(location.state?.setup) || !profileComplete(profile)
  const [editing, setEditing] = useState(Boolean(location.state?.setup))
  useEffect(() => { if (profile && setup) setEditing(true) }, [profile, setup])

  if (!profile) return <p className="muted">Loading…</p>

  return (
    <div className="narrow">
      {!setup && <Link className="backlink" to="/sme">← My tasks</Link>}
      <div className="page-head">
        <div>
          <h1>{setup ? 'Complete your profile' : 'Your profile'}</h1>
          <div className="meta">{setup ? 'Engineers see this next to your tasks. It takes a minute.' : `Member since ${fmtDay(profile.created_at)} · ${profile.email}`}</div>
        </div>
      </div>
      {editing
        ? <ProfileForm profile={profile} setup={setup} onSaved={(me) => { setProfile(me); if (setup) navigate('/sme', { replace: true }); else setEditing(false) }} onCancel={() => setEditing(false)} />
        : <ProfileOverview profile={profile} onEdit={() => setEditing(true)} />}
    </div>
  )
}

function ProfileOverview({ profile, onEdit }: { profile: User; onEdit: () => void }) {
  return (
    <>
      <section className="form-section profile-overview">
        <div className="profile-overview-head">
          <Avatar avatar={profile.avatar} name={profile.name || profile.email} size={96} />
          <div>
            <h2 style={{ margin: 0 }}>{profile.name || profile.email}</h2>
            <div className="muted">{profile.email}</div>
            <div className="muted small">Joined {fmtDay(profile.created_at)}</div>
          </div>
        </div>
        <dl className="kv" style={{ marginTop: '1.25rem' }}>
          <div><dt>Bio</dt><dd>{profile.bio || <span className="muted">—</span>}</dd></div>
          <div data-tour="profile-expertise"><dt>Expertise</dt><dd><span className="chip-list">{profileAreas(profile).map(area => <span className="chip" key={area}>{area}</span>)}</span></dd></div>
          <div><dt>Programs</dt><dd>{profile.programs?.length ? <span className="chip-list">{profile.programs.map((p) => <span key={p} className="chip">{p}</span>)}</span> : <span className="muted">—</span>}</dd></div>
        </dl>
      </section>
      <div className="form-footer"><button className="primary" onClick={onEdit}>Edit profile</button></div>
    </>
  )
}

function ProfileForm({ profile, setup, onSaved, onCancel }: { profile: User; setup: boolean; onSaved: (me: User) => void; onCancel: () => void }) {
  const [name, setName] = useState(profile.name ?? '')
  const [bio, setBio] = useState(profile.bio ?? '')
  const [areas, setAreas] = useState(profileAreas(profile))
  const [programs, setPrograms] = useState<string[]>(profile.programs ?? [])
  const [avatar, setAvatar] = useState<AvatarData | null>(profile.avatar ?? null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function save() {
    setError(null)
    if (!avatar) { setError('Please choose a picture or an emoji.'); return }
    if (bio.trim().length < 20) { setError('Please write a short bio (at least a sentence).'); return }
    if (!areas.length) { setError('Choose at least one area or profession.'); return }
    if (programs.length === 0) { setError('Please select at least one program you work in.'); return }
    setBusy(true)
    try {
      // The API assigns avatar color; only the user's choice belongs in an update.
      const body: Record<string, unknown> = { name: name.trim(), bio: bio.trim(), areas, programs,
        avatar: { kind: avatar.kind, value: avatar.value } }
      onSaved(await api.put<User>('/me', body))
    } catch (e) { setError(e instanceof ApiError ? e.message : (e as Error).message) }
    finally { setBusy(false) }
  }

  return (
    <>
      <section className="form-section profile-form">
        <div className="field"><span className="field-label">Profile picture</span><em className="hint">A photo or an emoji. Shown next to your name for engineers and in the header here.</em>
          <AvatarPicker value={avatar} onChange={setAvatar} name={name || profile.email} /></div>
        <label className="field"><span>Name</span><input type="text" value={name} onChange={(e) => setName(e.target.value)} /></label>
        <label className="field"><span>Short bio</span><em className="hint">Who you are professionally and what you work on. Two or three sentences.</em>
          <textarea value={bio} onChange={(e) => setBio(e.target.value)} maxLength={600} style={{ minHeight: 100 }} placeholder="Describe your work, experience, and the results you create." />
          <span className="muted tiny">{bio.length}/600</span></label>
        <div className="field" data-tour="profile-expertise"><span className="field-label">Areas / professions</span><em className="hint">Choose up to five areas that describe your work.</em><AreaPicker value={areas} onChange={setAreas} /></div>
        <div className="field"><span className="field-label">Software and tools</span><em className="hint">Choose the programs you know. Add a name if it is missing.</em>
          <ProgramChips value={programs} onChange={setPrograms} /></div>
      </section>
      {error && <div className="note err">{error}</div>}
      <div className="form-footer">
        <button className="primary" disabled={busy} onClick={save}>{setup ? 'Save and continue' : 'Save profile'}</button>
        {!setup && <button className="ghost" disabled={busy} onClick={onCancel}>Cancel</button>}
      </div>
    </>
  )
}
